import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";

const FORM_URL = "https://readdy.ai/api/form/da2lm72amq5jpe1hs01g";

const contactWays = [
  { icon: "ri-mail-line", title: "邮箱", value: "service@cartoon1st.example" },
  { icon: "ri-time-line", title: "在线时间", value: "每日 09:00 - 22:00" },
  { icon: "ri-chat-smile-line", title: "追更心愿", value: "告诉我们你想看的内容" },
  { icon: "ri-feedback-line", title: "建议反馈", value: "欢迎提出宝贵意见" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 卡通动漫第一页",
      description: "通过卡通动漫第一页的联系表单，告诉我们你的追更心愿与内容建议。",
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "contact-ld";
    document.getElementById("contact-ld")?.remove();
    document.head.appendChild(script);
    return () => {
      document.getElementById("contact-ld")?.remove();
    };
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐校验：若被机器人填写，则给出与正常提交一致的反馈，但不真正提交
    const honeypot = (fd.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的留言，我们会尽快回复！");
      form.reset();
      return;
    }

    const name = (fd.get("name") as string)?.trim() ?? "";
    const email = (fd.get("email") as string)?.trim() ?? "";
    const subject = (fd.get("subject") as string)?.trim() ?? "";
    const content = (fd.get("content") as string)?.trim() ?? "";
    if (!name || !email || !content) {
      setStatus("error");
      setMessage("请填写姓名、邮箱与留言内容后再提交。");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setMessage("留言内容不能超过 500 字。");
      return;
    }

    const payload = new URLSearchParams();
    payload.set("name", name);
    payload.set("email", email);
    if (subject) payload.set("subject", subject);
    payload.set("content", content);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(serverMsg || "");
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage(parsed?.meta?.message || "感谢你的留言，我们会尽快回复！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 卡通动漫第一页"
        description="与卡通动漫第一页取得联系，提交你的追更心愿、内容建议或合作咨询，我们会认真阅读每一条留言并尽快回复。"
        keywords="卡通动漫第一页,联系我们,追更心愿,内容建议"
      />
      <Navbar />

      <main className="bg-background-50 pt-16 md:pt-20">
        <div className="container-x">
          <nav className="flex items-center gap-2 py-6 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" style={{ fontSize: "16px" }}></i>
            <span className="text-foreground-800">联系我们</span>
          </nav>

          <section className="mb-8 rounded-2xl bg-gradient-to-r from-primary-500 to-accent-500 p-6 text-background-50 md:p-8" aria-label="联系页头">
            <h1 className="font-display text-2xl md:text-3xl">联系我们</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-50/90 md:text-base">
              想看的漫画没找到？有内容建议或合作意向？欢迎给我们留言，我们会认真阅读每一条反馈，尽量满足大家的追更需求。
            </p>
          </section>

          {/* 联系方式 */}
          <section className="grid grid-cols-1 gap-4 pb-10 sm:grid-cols-2 lg:grid-cols-4" aria-label="联系方式">
            {contactWays.map((w) => (
              <div key={w.title} className="rounded-xl bg-background-100 p-5 ring-1 ring-background-200">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className={w.icon} style={{ fontSize: "20px" }}></i>
                </span>
                <h2 className="mt-3 font-medium text-foreground-950">{w.title}</h2>
                <p className="mt-1 text-sm text-foreground-500">{w.value}</p>
              </div>
            ))}
          </section>

          {/* 留言表单 */}
          <section className="grid grid-cols-1 gap-8 pb-14 lg:grid-cols-3" aria-label="留言表单">
            <div className="lg:col-span-2">
              <form
                id="contact-form"
                data-readdy-form="联系我们"
                onSubmit={onSubmit}
                className="rounded-2xl bg-background-100 p-5 ring-1 ring-background-200 md:p-7"
              >
                <h2 className="font-display text-xl text-foreground-950">给我们留言</h2>
                <p className="mt-1 text-sm text-foreground-500">带 * 为必填项，留言内容请勿超过 500 字</p>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">你的称呼 *</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      maxLength={30}
                      className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="怎么称呼你"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">邮箱 *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={60}
                      className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="用于回复你"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="subject" className="mb-1.5 block text-sm text-foreground-700">留言主题</label>
                  <select
                    id="subject"
                    name="subject"
                    className="h-11 w-full rounded-lg border border-background-200 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="追更心愿">追更心愿</option>
                    <option value="内容建议">内容建议</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="content" className="mb-1.5 block text-sm text-foreground-700">留言内容 *</label>
                  <textarea
                    id="content"
                    name="content"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                    placeholder="想看的漫画、想推荐的作品，或对平台的任何建议……"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最长 500 字</p>
                </div>

                {/* 蜜罐字段（供反垃圾校验，对用户隐藏） */}
                <div className="hp-guard" aria-hidden="true">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                </div>

                <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    className="w-full rounded-full bg-primary-500 px-8 py-3 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600 transition-colors sm:w-auto"
                  >
                    提交留言 <i className="ri-send-plane-fill" style={{ fontSize: "14px" }}></i>
                  </button>
                  {status === "success" && (
                    <p className="flex items-center gap-1.5 text-sm text-primary-700" role="status">
                      <i className="ri-checkbox-circle-fill" style={{ fontSize: "16px" }}></i> {message}
                    </p>
                  )}
                  {status === "error" && (
                    <p className="flex items-center gap-1.5 text-sm text-primary-700" role="alert">
                      <i className="ri-error-warning-fill" style={{ fontSize: "16px" }}></i> {message}
                    </p>
                  )}
                </div>
              </form>
            </div>

            <aside className="space-y-4" aria-label="联系须知">
              <div className="rounded-xl bg-background-100 p-5 ring-1 ring-background-200">
                <h3 className="flex items-center gap-2 font-medium text-foreground-950">
                  <i className="ri-question-line text-primary-600" style={{ fontSize: "18px" }}></i> 常见问题
                </h3>
                <ul className="mt-3 space-y-3 text-sm text-foreground-600">
                  <li className="flex gap-2"><span className="text-primary-600">·</span> 想看的漫画没找到？告诉我们名字，我们会尽快补充。</li>
                  <li className="flex gap-2"><span className="text-primary-600">·</span> 想看更多某类题材？留言说明即可。</li>
                  <li className="flex gap-2"><span className="text-primary-600">·</span> 反馈会在 1-3 个工作日内回复到你的邮箱。</li>
                </ul>
              </div>
              <div className="rounded-xl bg-secondary-50 p-5 ring-1 ring-secondary-200">
                <h3 className="flex items-center gap-2 font-medium text-foreground-950">
                  <i className="ri-heart-line text-secondary-600" style={{ fontSize: "18px" }}></i> 想先看看内容？
                </h3>
                <p className="mt-2 text-sm text-foreground-600">也可以先去逛逛今日更新、热门榜单和编辑推荐，找到喜欢的漫画再留言分享。</p>
                <Link to="/today" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors">
                  去今日更新 <i className="ri-arrow-right-line" style={{ fontSize: "14px" }}></i>
                </Link>
              </div>
            </aside>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}